import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Autocomplete,
  FilledTextFieldProps,
  FormControl,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField,
  TextFieldVariants,
} from '@mui/material';

interface WholesaleAgentAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  options: Array<string>;
  required?: boolean;
  label?: string;
}

export function WholesaleAgentAutocomplete({
  value,
  onChange,
  error,
  helperText,
  disabled,
  options,
  required = false,
  label = 'Wholesale Agent',
}: WholesaleAgentAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleOnChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, data: string) => {
      if (data === 'Other') {
        onChange('');
        setInputValue('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } else {
        onChange(data || '');
        setInputValue(data || '');
      }
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, newInputValue: string) => {
      setInputValue(newInputValue);
      onChange(newInputValue);
    },
    [onChange],
  );

  const renderInput = useCallback(
    (
      params: React.JSX.IntrinsicAttributes & { variant?: TextFieldVariants } & Omit<
          FilledTextFieldProps | OutlinedTextFieldProps | StandardTextFieldProps,
          'variant'
        >,
    ) => (
      <TextField
        {...params}
        inputRef={inputRef}
        label={label}
        required={required}
        helperText={helperText}
        error={!!error}
        data-testid="wholesale-agent"
      />
    ),
    [label, required, helperText, error],
  );

  return (
    <FormControl error={!!error} fullWidth>
      <Autocomplete
        freeSolo
        options={options}
        disabled={disabled}
        value={value || ''}
        inputValue={inputValue}
        onChange={handleOnChange}
        onInputChange={handleInputChange}
        renderInput={renderInput}
      />
    </FormControl>
  );
}
